import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import _ from 'lodash';
import { useUserPreferences } from 'hooks/useUserPreferences';

interface PinnedActionsContextType {
    pinnedActions: string[];
    pendingPinnedActions: string[];
    togglePinAction: (actionId: string) => void;
    canPinMore: boolean;
    applyPinnedActions: () => void;
    loading: boolean;
}

const PinnedActionsContext = createContext<PinnedActionsContextType | undefined>(undefined);

export const PinnedActionsProvider = ({
    children,
    recordTypeName,
    recordTypeId
}: {
    children: ReactNode;
    recordTypeName?: string;
    recordTypeId?: number;
}) => {
    // Hook para manejar las preferencias de usuario con Apollo
    const {
        pinnedActions: serverPinnedActions,
        savePreferences,
        loading: preferencesLoading
    } = useUserPreferences(recordTypeName, recordTypeId);

    // Estado local para las acciones pendientes (antes de guardar)
    const [pendingPinnedActions, setPendingPinnedActions] = useState<string[]>([]);

    // Inicializar con datos del servidor únicamente
    useEffect(() => {
        setPendingPinnedActions(serverPinnedActions);
    }, [serverPinnedActions]);

    const debouncedSavePreferences = useMemo(
        () =>
            _.debounce(async (newPinnedActions: string[]) => {
                if (recordTypeName && recordTypeId) {
                    await savePreferences(newPinnedActions);
                }
            }, 1000),
        [savePreferences, recordTypeName, recordTypeId]
    );

    const togglePinAction = useCallback(
        (actionId: string) => {
            const newPendingPinnedActions = pendingPinnedActions.includes(actionId)
                ? pendingPinnedActions.filter((id) => id !== actionId)
                : [...pendingPinnedActions, actionId];

            setPendingPinnedActions(newPendingPinnedActions);
            debouncedSavePreferences(newPendingPinnedActions);
        },
        [pendingPinnedActions, debouncedSavePreferences]
    );

    const applyPinnedActions = useCallback(() => {
        console.log('PinnedActionsProvider - Apply pinned actions called');
        debouncedSavePreferences.flush();
    }, [debouncedSavePreferences]);

    const value = {
        pinnedActions: pendingPinnedActions, // Usar pendingPinnedActions como estado real
        pendingPinnedActions,
        togglePinAction,
        canPinMore: pendingPinnedActions.length < 2,
        applyPinnedActions,
        loading: preferencesLoading
    };

    return <PinnedActionsContext.Provider value={value}>{children}</PinnedActionsContext.Provider>;
};

export const usePinnedActions = (): PinnedActionsContextType => {
    const context = useContext(PinnedActionsContext);
    if (context === undefined) {
        throw new Error('usePinnedActions must be used within a PinnedActionsProvider');
    }
    return context;
};
